// src/components/chatbot/VoiceOptions.tsx - UPDATED VERSION
import React, { useState, useRef, useEffect } from 'react';
import { Mic, Volume2, MessageSquare, Settings, HelpCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';

// Import Voice AI Workforce components and types
import { 
  VoiceButton, 
  VoiceCommand, 
  VoiceResponse, 
  VoiceAIConfig, 
  AIProvider, 
  SpeechProvider, 
  ResponseMode 
} from '@voice-ai-workforce/react';
import { PUBLIC_OPENAI_API_KEY } from '@/config/env';

type VoiceMode = 'voice-to-voice' | 'voice-to-text' | 'disabled';

type VoiceOptionsProps = {
  isVisible: boolean;
  onClose: () => void;
  onVoiceCommand: (command: VoiceCommand, mode: VoiceMode) => void;
  onVoiceResponse: (response: VoiceResponse) => void;
  currentMode: VoiceMode;
  onModeChange: (mode: VoiceMode) => void;
  onShowTips: () => void;
  voiceConfig?: VoiceAIConfig;
};

const VoiceOptions: React.FC<VoiceOptionsProps> = ({
  isVisible,
  onClose,
  onVoiceCommand,
  onVoiceResponse,
  currentMode,
  onModeChange,
  onShowTips,
  voiceConfig
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  // Default Voice AI configuration
  const defaultVoiceConfig: VoiceAIConfig = {
    businessContext: {
      name: "Staffluent",
      domain: "workforce_management",
      capabilities: ["time tracking", "task management", "team coordination"]
    },
    aiProviders: {
      primary: {
        provider: AIProvider.OPENAI,
        apiKey: PUBLIC_OPENAI_API_KEY,
        model: 'gpt-3.5-turbo'
      },
      fallbacks: [{
        provider: AIProvider.KEYWORDS,
        fallbackMode: true
      }]
    },
    speechToText: {
      provider: SpeechProvider.WEB_SPEECH,
      language: 'en-US',
      continuous: false
    },
    textToSpeech: {
      provider: SpeechProvider.WEB_SPEECH,
      speed: 1.0
    },
    responseMode: ResponseMode.BOTH,
    interfaceMode: 'end-user',
    confidenceThreshold: 0.7
  };

  // Use provided config or default
  const activeVoiceConfig = voiceConfig || defaultVoiceConfig;

  // Voice-to-voice configuration
  const voiceToVoiceConfig: VoiceAIConfig = {
    ...activeVoiceConfig,
    responseMode: ResponseMode.BOTH
  };

  // Voice-to-text configuration
  const voiceToTextConfig: VoiceAIConfig = {
    ...activeVoiceConfig,
    responseMode: ResponseMode.TEXT
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isVisible, onClose]);

  // Handle voice command - receives VoiceCommand object
  const handleVoiceCommand = (command: VoiceCommand) => {
    console.log('Voice command received:', command);
    onVoiceCommand(command, currentMode);
    setIsListening(false);
  };
  
  // Handle voice response - receives VoiceResponse object
  const handleVoiceResponse = (response: VoiceResponse) => {
    console.log('Voice response received:', response);
    onVoiceResponse(response);
  };

  // Handle voice error
  const handleError = (error: any) => {
    console.error('Voice AI error:', error);
    setIsListening(false);
  };

  // Handle voice state changes
  const handleVoiceStateChange = (state: any) => {
    setIsListening(state.isListening || false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center p-4 pointer-events-none">
      <div 
        ref={modalRef}
        className="pointer-events-auto w-full max-w-md"
      >
        <Card className="shadow-2xl border-2 bg-white/95 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-full">
                  <Mic className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-sm">Voice Assistant</CardTitle>
                  <CardDescription className="text-xs">
                    Choose how you want to interact
                  </CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onShowTips}
                  className="h-7 w-7 p-0"
                  title="Show Voice Tips"
                >
                  <HelpCircle className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-7 w-7 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-4 pb-4">
            {/* Quick Mode Selection */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={currentMode === 'voice-to-voice' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onModeChange('voice-to-voice')}
                className="flex flex-col items-center gap-1 h-auto py-3"
              >
                <Volume2 className="h-4 w-4" />
                <span className="text-xs">Voice Chat</span>
              </Button>
              <Button
                variant={currentMode === 'voice-to-text' ? 'default' : 'outline'}
                size="sm"
                onClick={() => onModeChange('voice-to-text')}
                className="flex flex-col items-center gap-1 h-auto py-3"
              >
                <MessageSquare className="h-4 w-4" />
                <span className="text-xs">Voice Input</span>
              </Button>
            </div>

            {/* Voice Buttons */}
            {currentMode !== 'disabled' && (
              <div className="flex items-center justify-center">
                <VoiceButton
                  config={currentMode === 'voice-to-voice' ? voiceToVoiceConfig : voiceToTextConfig}
                  size="lg"
                  variant="primary"
                  onCommand={handleVoiceCommand}
                  onResponse={handleVoiceResponse}
                  onError={handleError}
                  // @ts-ignore
                  onStateChange={handleVoiceStateChange}
                  showMiniCenter={false}
                  className="w-16 h-16"
                />
              </div>
            )}

            {/* Mode Description */}
            <div className="text-center">
              {currentMode === 'voice-to-voice' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-green-600">Voice Conversation Active</p>
                  <p className="text-xs text-muted-foreground">
                    Speak naturally and hear instant responses
                  </p>
                </div>
              )}
              {currentMode === 'voice-to-text' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-blue-600">Voice Input Active</p>
                  <p className="text-xs text-muted-foreground">
                    Speak your questions, read the answers
                  </p>
                </div>
              )}
              {currentMode === 'disabled' && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Voice Disabled</p>
                  <p className="text-xs text-muted-foreground">
                    Use keyboard input only
                  </p>
                </div>
              )}
            </div>

            {/* Expandable Advanced Options */}
            <div className="border-t pt-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full justify-between text-xs"
              >
                <span className="flex items-center gap-2">
                  <Settings className="h-3 w-3" />
                  Advanced Options
                </span>
                <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                  ↓
                </span>
              </Button>

              {isExpanded && (
                <div className="mt-3 space-y-3 border-t pt-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <p className="text-xs font-medium">Enable Voice Features</p>
                      <p className="text-xs text-muted-foreground">
                        Turn voice assistance on/off
                      </p>
                    </div>
                    <Switch
                      checked={currentMode !== 'disabled'}
                      onCheckedChange={(checked) => 
                        onModeChange(checked ? 'voice-to-text' : 'disabled')
                      }
                    />
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <p className="text-xs font-medium">Quick Examples</p>
                    <div className="flex flex-wrap gap-1">
                      {[
                        'Show my tasks',
                        'Team status',
                        'Clock me in',
                        'Help me'
                      ].map((example) => (
                        <Badge 
                          key={example} 
                          variant="outline" 
                          className="text-xs cursor-pointer hover:bg-muted"
                          onClick={() => {
                            const mockCommand: VoiceCommand = {
                              intent: example.toLowerCase().replace(/\s+/g, '_'),
                              entities: {},
                              confidence: 0.9,
                              rawText: example,
                              timestamp: new Date()
                            };
                            handleVoiceCommand(mockCommand);
                          }}
                        >
                          "{example}"
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Quick Help */}
            <div className="text-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowTips}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                <HelpCircle className="h-3 w-3 mr-1" />
                Need help? View voice guide
              </Button>
            </div>

            {/* Listening Indicator */}
            {isListening && (
              <div className="flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                <div className="flex space-x-1">
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '200ms' }}></div>
                  <div className="h-2 w-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '400ms' }}></div>
                </div>
                <span className="text-sm text-blue-700 font-medium">Listening... Speak now!</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoiceOptions;